import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import PackageCard from './PackageCard'
import ModalForm from './ModalForm'

/**
 * Our Talents Section - Premium Web Development Showcase
 * 
 * Features:
 * - Web Development talent card with custom-built approach
 * - No templates, no scripts, no plugins - pure custom development
 * - Enterprise security and full customization
 * - Portfolio, LMS, E-commerce, Systems, Apps, Automation, Chatbots
 * - Complete infrastructure management
 * 
 * Talent Focus:
 * - Custom development only
 * - Enterprise-grade security
 * - Full infrastructure management
 * - No hidden charges
 * - Focus on features and monetization, not bugs
 * 
 * Animation Timeline:
 * - Card animates in with luxury effects
 * - Hover effects with glow and scale animations
 * - Modal integration for talent inquiries
 */

export default function PackagesSection() {
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isArabic, setIsArabic] = useState(false)
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  
  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  
  // Show scroll to top button when scrolled
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  
  // Talent data with premium positioning
  const talents = {
    english: [
      {
        title: 'Web Development',
        tagline: 'We develop what we custom-create',
        bullets: [
          'Pure custom development from scratch',
          'Zero reliance on third-party templates or frameworks',
          'Enterprise-grade security architecture',
          'Built exclusively for discerning clients who value quality',
          'Designed for visionaries who demand excellence',
          'Portfolio • LMS • E-commerce • Systems • Apps • Automation • Chatbots',
          'Your imagination is our only limitation',
          'Every line of code tailored to your unique requirements',
          'Transparent pricing with no surprise costs',
          'Focus on growth and revenue, not technical headaches',
          'We transform your vision into digital reality'
        ],
        ctaLabel: 'Discuss Your Vision'
      },
      {
        title: 'Marketing',
        tagline: 'We market what we strategically craft',
        bullets: [
          'No generic marketing templates or cookie-cutter campaigns',
          'Zero reliance on outdated marketing automation tools',
          'Strategic marketing architecture built for your unique brand',
          'Created exclusively for brands that understand true marketing value',
          'Designed for businesses who demand measurable results',
          'Brand Strategy • Content Marketing • Social Media • SEO • PPC • Email Marketing • Analytics',
          'Your market position is our strategic foundation',
          'Every campaign tailored to your specific audience and goals',
          'Focus on growth and market dominance, not vanity metrics',
          'We transform your brand into market leadership'
        ],
        ctaLabel: 'Discuss Your Strategy'
      },
      {
        title: 'Design & Video',
        tagline: 'We create what we artistically envision',
        bullets: [
          'No stock templates or generic design libraries',
          'Zero reliance on pre-made video templates or effects',
          'Custom design architecture built for your unique visual identity',
          'Created exclusively for brands that understand true design value',
          'Designed for visionaries who demand visual excellence',
          'Brand Identity • Logo Design • UI/UX • Video Production • Motion Graphics • 3D Animation',
          'Your visual story is our creative foundation',
          'Every design tailored to your specific brand and audience',
          'We transform your vision into visual masterpieces'
        ],
        ctaLabel: 'Discuss Your Vision'
      }
    ],
    arabic: [
      {
        title: 'تطوير المواقع',
        tagline: 'نطور ما نبتكره خصيصاً',
        bullets: [
          'مفيش تمبلات جاهزة - مفيش كود منسوخ',
          'كل مشروع بالكامل مخصص ليك ولإحتياجك',
          'هنا المكان الى تقدر تعرف ايه الفرق بين البرمجة الحقيقة وبين المشاريع الوهمية',
          'سواء كنت فرد أو شركة أو علامة تجارية، هنفهم احتياجك',
          'مدرس عايز يعمل منصة تعليم أونلاين؟ هنعملك LMS متكامل',
          'صاحب متجر عايز يبيع أونلاين؟ هنعملك متجر إلكتروني احترافي',
          'شركة عايزة نظام إدارة داخلي؟ هنعملك نظام مخصص لشغلك',
          'حد عايز يعمل تطبيق موبايل؟ هنعملك تطبيق احترافي',
          'صاحب قناة يوتيوب عايز موقع لقناته؟ هنعملك موقع يعرض فيديوهاتك',
          'مواقع شخصية • منصات تعليم • متاجر إلكترونية • أنظمة إدارة • تطبيقات • أتمتة • شات بوتات',
          'خيالك هو اللي يحدد إيه اللي ممكن نعمله',
          'كل كود مكتوب خصيصاً ليك',
          'نحققلك اللي بتفكر فيه'
        ],
        ctaLabel: 'ناقش رؤيتك'
      },
      {
        title: 'التسويق',
        tagline: 'نسوق ما نخططه استراتيجياً',
        bullets: [
          'سواء كنت فرد أو شركة أو علامة تجارية، هنفهم احتياجك',
          'صاحب متجر عايز يزيد المبيعات؟ هنعملك استراتيجية تسويق متكاملة',
          'مؤثر عايز يطور علامته التجارية؟ هنعملك خطة تسويق محتوى احترافية',
          'شركة عايزة تزيد الوعي بعلامتها؟ هنعملك حملات إعلانية مؤثرة',
          'حد عايز يبيع منتج جديد؟ هنعملك خطة تسويق من الصفر',
          'صاحب قناة يوتيوب عايز يطور قناته؟ هنعملك استراتيجية نمو',
          'استراتيجية العلامة التجارية • تسويق المحتوى • السوشيال ميديا • السيو • الإعلانات المدفوعة • الإيميل ماركتنج • التحليلات',
          'موقعك في السوق ده أساس شغلنا',
          'نخلي علامتك التجارية تبقى رقم واحد في السوق'
        ],
        ctaLabel: 'ناقش استراتيجيتك'
      },
      {
        title: 'التصميم والفيديو',
        tagline: 'نبتكر ما نتخيله فنياً',
        bullets: [
          'دكتور ومحتاج تعمل فيديوهات أو هوية كاملة ليك ولعيادتك أو مركزك الطبى',
          'ومحتاج حد يكون فاهم المصطلحات الطبية هتلاقى عندنا متخصصين فى التسويق الطبى والتصميمات',
          'مش محتاج تشغل بالك بأخطاء اللغة أو خطأ فى المصطلح الطبى ممكن يأثر على رسالتك',
          'سواء كنت فرد أو شركة أو علامة تجارية، هنفهم احتياجك',
          'مدرس عايز يعمل كورس أونلاين؟ هنعملك محتوى تعليمي احترافي',
          'يوتيوبر عايز يطور قناته؟ هنعملك فيديوهات جذابة ومحتوى بصري',
          'شركة عايزة فيديوهات تسويقية؟ هنعملك حملات إعلانية مؤثرة',
          'حد من البيت بيحاول يسوق لمنتج دروب شيبينج؟ هنعملك محتوى تسويقي بسيط وفعال',
          'صانع محتوى هاند ميد عايز يعرض منتجاته؟ هنعملك فيديوهات تعرض المنتج بشكل احترافي',
          'هوية العلامة التجارية • تصميم الشعار • واجهة المستخدم • إنتاج الفيديو • الرسوم المتحركة • الرسوم ثلاثية الأبعاد',
          'قصتك الإبداعية دي أساس فننا',
          'نحول رؤيتك إلى أعمال فنية حقيقية'
        ],
        ctaLabel: 'ناقش رؤيتك'
      }
    ]
  }
  
  const handleCTAClick = (talentName: string) => {
    setSelectedPackage(talentName)
    setIsModalOpen(true)
  }
  
  const handleModalClose = () => {
    setIsModalOpen(false)
    setSelectedPackage(null)
  }
  
  return (
    <>
      <section className="py-20 md:py-32 bg-luxury-black relative overflow-hidden scroll-snap-start" id="talents-section">
        {/* Gradient Transition from Hero */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-luxury-black/50 pointer-events-none"></div>
        {/* Animated Background Elements */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Animated Gradient Background */}
          <motion.div
            className="absolute inset-0 opacity-30"
            animate={{
              background: [
                "radial-gradient(circle at 20% 50%, rgba(212, 175, 55, 0.1) 0%, transparent 50%)",
                "radial-gradient(circle at 80% 20%, rgba(212, 175, 55, 0.15) 0%, transparent 50%)",
                "radial-gradient(circle at 40% 80%, rgba(212, 175, 55, 0.1) 0%, transparent 50%)",
                "radial-gradient(circle at 20% 50%, rgba(212, 175, 55, 0.1) 0%, transparent 50%)"
              ]
            }}
            transition={{ duration: isMobile ? 12 : 8, repeat: Infinity, ease: "easeInOut" }}
          />
          
          {/* Floating Code Snippets */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 0.15, scale: 1 }}
            transition={{ duration: 2, delay: 0.5 }}
            className="absolute top-20 left-10 text-luxury-gold/25 text-xs font-mono"
            style={{ fontFamily: "'Fira Code', 'Courier New', monospace" }}
          >
            <motion.div
              animate={{ 
                y: [0, -15, 0],
                rotate: [0, 2, 0]
              }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            >
              {`<div className="talent-card">`}
            </motion.div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 0.1, scale: 1 }}
            transition={{ duration: 2, delay: 1 }}
            className="absolute top-40 right-20 text-luxury-gold/20 text-xs font-mono"
            style={{ fontFamily: "'Fira Code', 'Courier New', monospace" }}
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              {`const talent = "amazing";`}
            </motion.div>
          </motion.div>

          {/* Marketing Charts */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 0.1, x: 0 }}
            transition={{ duration: 2, delay: 1.5 }}
            className="absolute top-60 left-10 text-luxury-gold/20"
          >
            <motion.div
              animate={{ rotate: [0, 5, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="w-16 h-16 border border-luxury-gold/20 rounded-full flex items-center justify-center"
            >
              <div className="w-8 h-8 bg-luxury-gold/10 rounded-full"></div>
            </motion.div>
          </motion.div>

          {/* Design Tools */}
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 0.1, scale: 1 }}
            transition={{ duration: 2, delay: 2 }}
            className="absolute bottom-20 right-10 text-luxury-gold/20"
          >
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 border-2 border-luxury-gold/20 rounded-full flex items-center justify-center"
            >
              <div className="w-6 h-6 border border-luxury-gold/20 rounded-full"></div>
            </motion.div>
          </motion.div>

          {/* Floating Particles */}
          {[...Array(isMobile ? 5 : 20)].map((_, i) => (
            <motion.div
              key={i}
              className={`absolute w-1 h-1 bg-luxury-gold/20 rounded-full ${isMobile ? 'mobile-particles' : ''}`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, isMobile ? -15 : -30, 0],
                x: [0, Math.random() * (isMobile ? 10 : 20) - (isMobile ? 5 : 10), 0],
                opacity: [0.2, isMobile ? 0.4 : 0.8, 0.2],
                scale: [0.5, isMobile ? 1.2 : 1.5, 0.5]
              }}
              transition={{
                duration: isMobile ? 2 + Math.random() * 2 : 3 + Math.random() * 4,
                repeat: Infinity,
                delay: Math.random() * (isMobile ? 1 : 2),
                ease: "easeInOut"
              }}
            />
          ))}
          
          {/* Geometric Patterns */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 0.1 }}
            transition={{ duration: 3 }}
            className="absolute inset-0"
          >
            <motion.div 
              className={`absolute top-1/4 left-1/4 w-32 h-32 border border-luxury-gold/15 rotate-45 ${isMobile ? 'three-d-elements' : ''}`}
              animate={{ rotate: [45, 405] }}
              transition={{ duration: isMobile ? 30 : 20, repeat: Infinity, ease: "linear" }}
            />
            <motion.div 
              className={`absolute top-3/4 right-1/4 w-24 h-24 border border-luxury-gold/15 rotate-12 ${isMobile ? 'three-d-elements' : ''}`}
              animate={{ rotate: [12, 372] }}
              transition={{ duration: isMobile ? 25 : 15, repeat: Infinity, ease: "linear" }}
            />
            <motion.div 
              className={`absolute bottom-1/4 left-1/3 w-20 h-20 border border-luxury-gold/15 rotate-75 ${isMobile ? 'three-d-elements' : ''}`}
              animate={{ rotate: [75, 435] }}
              transition={{ duration: isMobile ? 35 : 25, repeat: Infinity, ease: "linear" }}
            />
          </motion.div>
          
          {/* Floating Talent Icons */}
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 0.1, scale: 1 }}
            transition={{ duration: 2, delay: 2.5 }}
            className="absolute top-1/3 right-1/4"
          >
            <motion.div
              animate={{ 
                y: [0, isMobile ? -10 : -20, 0],
                rotate: [0, 360]
              }}
              transition={{ 
                y: { duration: isMobile ? 6 : 4, repeat: Infinity, ease: "easeInOut" },
                rotate: { duration: isMobile ? 15 : 10, repeat: Infinity, ease: "linear" }
              }}
              className={`w-16 h-16 border-2 border-luxury-gold/20 rounded-full flex items-center justify-center ${isMobile ? 'three-d-elements' : ''}`}
            >
              <div className={`w-8 h-8 bg-luxury-gold/10 rounded-full ${isMobile ? 'three-d-elements' : ''}`}></div>
            </motion.div>
          </motion.div>
          
          {/* Marketing Data Visualization */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 0.1, x: 0 }}
            transition={{ duration: 2, delay: 3 }}
            className="absolute bottom-1/3 left-1/4"
          >
            <motion.div
              animate={{ 
                scale: [1, isMobile ? 1.05 : 1.1, 1],
                opacity: [0.1, isMobile ? 0.2 : 0.3, 0.1]
              }}
              transition={{ duration: isMobile ? 4 : 3, repeat: Infinity, ease: "easeInOut" }}
              className={`w-20 h-20 border border-luxury-gold/20 rounded-full flex items-center justify-center ${isMobile ? 'three-d-elements' : ''}`}
            >
              <div className={`w-12 h-12 border border-luxury-gold/20 rounded-full flex items-center justify-center ${isMobile ? 'three-d-elements' : ''}`}>
                <div className={`w-6 h-6 bg-luxury-gold/20 rounded-full ${isMobile ? 'three-d-elements' : ''}`}></div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            {/* Language Toggle */}
            <div className="flex justify-center mb-8">
              <div className="bg-luxury-black/50 backdrop-blur-sm rounded-full p-1 border border-luxury-gold/30">
                <button
                  onClick={() => setIsArabic(false)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    !isArabic 
                      ? 'bg-luxury-gold text-luxury-black' 
                      : 'text-luxury-gold hover:text-white'
                  }`}
                >
                  English
                </button>
                <button
                  onClick={() => setIsArabic(true)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    isArabic 
                      ? 'bg-luxury-gold text-luxury-black' 
                      : 'text-luxury-gold hover:text-white'
                  }`}
                >
                  العربية
                </button>
              </div>
            </div>

                <motion.h2 
                  className="text-4xl md:text-5xl font-bold text-luxury-gold mb-6"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.5 }}
                  style={{
                    fontFamily: isArabic 
                      ? "'Amiri', 'Scheherazade New', 'Noto Naskh Arabic', serif" 
                      : "'Inter', 'Helvetica Neue', sans-serif"
                  }}
                >
                  {isArabic ? 'مواهبنا' : 'Our Talents'}
                </motion.h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {isArabic 
                ? 'تطوير مخصص يتجاوز التوقعات. نبني ما تتخيله.' 
                : 'Custom development that exceeds expectations. We build what you imagine.'
              }
            </p>
          </motion.div>
          
              {/* Talent Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 max-w-7xl mx-auto">
                {talents[isArabic ? 'arabic' : 'english'].map((talent, index) => (
                  <motion.div
                    key={talent.title}
                    initial={{ 
                      opacity: 0, 
                      y: 100,
                      scale: 0.8,
                      rotateX: 45
                    }}
                    whileInView={{ 
                      opacity: 1, 
                      y: 0,
                      scale: 1,
                      rotateX: 0
                    }}
                    transition={{ 
                      duration: isMobile ? 0.6 : 1, 
                      delay: index * (isMobile ? 0.1 : 0.2),
                      ease: "easeOut",
                      type: "spring",
                      stiffness: isMobile ? 80 : 100
                    }}
                    viewport={{ once: true, margin: "-100px" }}
                    className={`flex justify-center ${isMobile ? 'mobile-touch-optimized' : ''}`}
                    whileHover={!isMobile ? {
                      y: -10,
                      scale: 1.02,
                      transition: { duration: 0.3 }
                    } : {}}
                  >
                <PackageCard
                  title={talent.title}
                  tagline={talent.tagline}
                  bullets={talent.bullets}
                  ctaLabel={talent.ctaLabel}
                  highlight={true}
                  onCTAClick={handleCTAClick}
                />
              </motion.div>
            ))}
          </div>
          
          {/* Section Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <div className="text-center space-y-8">
              {/* Main Tagline with Stunning Animation */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.2 }}
                className="relative"
              >
                <motion.p 
                  className="text-cyan-300 text-2xl md:text-3xl font-medium italic mb-4"
                  style={{
                    fontFamily: "'Playfair Display', 'Cormorant Garamond', serif",
                    fontWeight: "600",
                    lineHeight: "1.3"
                  }}
                  animate={{
                    textShadow: [
                      "0 0 20px rgba(34, 211, 238, 0.5)",
                      "0 0 30px rgba(34, 211, 238, 0.8)",
                      "0 0 20px rgba(34, 211, 238, 0.5)"
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                >
                  {isArabic 
                    ? 'تطوير مخصص — نبني ما تتخيله' 
                    : 'Custom development — we build what you imagine'
                  }
                </motion.p>
                
                {/* Animated Underline */}
                <motion.div
                  className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 h-1 bg-gradient-to-r from-transparent via-cyan-300 to-transparent"
                  initial={{ width: 0 }}
                  whileInView={{ width: "70%" }}
                  transition={{ duration: 1.5, delay: 0.5 }}
                />
              </motion.div>
              
              {/* Stunning Content Container */}
              <motion.div 
                className="max-w-5xl mx-auto relative"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.4 }}
              >
                {/* Background Glow Effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-cyan-300/5 via-purple-400/5 to-pink-400/5 rounded-2xl blur-xl"
                  animate={{
                    opacity: [0.3, 0.6, 0.3],
                    scale: [1, 1.05, 1]
                  }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                />
                
                {/* Content with Amazing Typography */}
                <div className="relative z-10 p-6 md:p-8 space-y-8">
                  {/* Main Statement */}
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.6 }}
                    className="relative"
                  >
                    <motion.p 
                      className="text-cyan-300 text-xl md:text-2xl leading-relaxed font-medium text-center max-w-4xl mx-auto"
                      style={{
                        fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
                        fontWeight: "500",
                        lineHeight: "1.6"
                      }}
                      animate={{
                        textShadow: [
                          "0 0 15px rgba(34, 211, 238, 0.4)",
                          "0 0 25px rgba(34, 211, 238, 0.6)",
                          "0 0 15px rgba(34, 211, 238, 0.4)"
                        ]
                      }}
                      transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                    >
                      {isArabic 
                        ? 'مشاريع عملائنا السابقة ملكيتهم الخاصة وليس للعرض هنا'
                        : 'Project code is our clients asset'
                      }
                    </motion.p>
                    
                    {/* Floating Particles around text */}
                    {[...Array(6)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-1 h-1 bg-cyan-300/40 rounded-full"
                        style={{
                          left: `${20 + (i * 15)}%`,
                          top: `${10 + (i % 2) * 20}%`,
                        }}
                        animate={{
                          y: [0, -10, 0],
                          opacity: [0.4, 0.8, 0.4],
                          scale: [0.5, 1, 0.5]
                        }}
                        transition={{
                          duration: 2 + i * 0.3,
                          repeat: Infinity,
                          delay: i * 0.2,
                          ease: "easeInOut"
                        }}
                      />
                    ))}
                  </motion.div>
                  
                  {/* Secondary Statement */}
                  <motion.div
                    initial={{ opacity: 0, x: isArabic ? 30 : -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 1, delay: 0.8 }}
                    className="relative"
                  >
                    <motion.p 
                      className="text-white/90 text-lg md:text-xl leading-relaxed text-center max-w-3xl mx-auto"
                      style={{
                        fontFamily: isArabic 
                          ? "'Amiri', 'Scheherazade New', 'Noto Naskh Arabic', serif" 
                          : "'Inter', 'Helvetica Neue', sans-serif",
                        lineHeight: "1.7"
                      }}
                    >
                      {isArabic 
                        ? 'نحن نحترم ما قد تم إنجازة لهم • قد تكون قادم من هناك أو أنت الأن داخل فن من ما نستطيع إنجازة'
                        : 'We respect what has been accomplished for them • You might be coming from there or you are now inside art of what we can achieve'
                      }
                    </motion.p>
                  </motion.div>
                  
                  {/* Tech Stack with Amazing Animation */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1 }}
                    className="relative"
                  >
                    <motion.div
                      className="inline-block"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.3 }}
                    >
                      <motion.p 
                        className="text-white/80 text-base md:text-lg font-mono tracking-wider text-center mb-4"
                        style={{
                          fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
                          fontWeight: "500"
                        }}
                        animate={{
                          color: [
                            "rgba(255, 255, 255, 0.8)",
                            "rgba(255, 255, 255, 1.0)",
                            "rgba(255, 255, 255, 0.8)"
                          ]
                        }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                      >
                        {isArabic 
                          ? 'هذا الفن تم بناءه باستخدام'
                          : 'This portfolio built by modern customized tech'
                        }
                      </motion.p>
                      
                      {/* Animated Tech Stack */}
                      <motion.div 
                        className="mt-6 flex flex-wrap justify-center gap-4"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.2 }}
                      >
                        {['tailwind', 'vite', 'react', 'express', 'nodejs', 'three.js', 'framer-motion'].map((tech, index) => (
                          <motion.span
                            key={tech}
                            className="px-4 py-2 bg-gradient-to-r from-cyan-300/10 to-purple-400/10 border border-cyan-300/30 rounded-full text-sm font-mono text-cyan-300/80"
                            style={{
                              fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace"
                            }}
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: 1.4 + index * 0.1 }}
                            whileHover={{ 
                              scale: 1.1, 
                              backgroundColor: "rgba(34, 211, 238, 0.2)",
                              borderColor: "rgba(34, 211, 238, 0.6)"
                            }}
                            animate={{
                              boxShadow: [
                                "0 0 5px rgba(34, 211, 238, 0.2)",
                                "0 0 15px rgba(34, 211, 238, 0.4)",
                                "0 0 5px rgba(34, 211, 238, 0.2)"
                              ]
                            }}
                          >
                            {tech}
                          </motion.span>
                        ))}
                      </motion.div>
                    </motion.div>
                  </motion.div>
                </div>
                
                {/* Decorative Elements */}
                <motion.div
                  className="absolute -top-4 -left-4 w-8 h-8 border border-cyan-300/20 rotate-45"
                  animate={{ rotate: [45, 405] }}
                  transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                />
                <motion.div
                  className="absolute -bottom-4 -right-4 w-6 h-6 border border-purple-400/20 rotate-12"
                  animate={{ rotate: [12, 372] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                />
                <motion.div
                  className="absolute top-1/2 -right-8 w-4 h-4 bg-gradient-to-r from-cyan-300/20 to-purple-400/20 rounded-full"
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.2, 0.6, 0.2]
                  }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 z-40 bg-luxury-gold text-luxury-black p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>
      
      {/* Modal Form */}
          <ModalForm
            isOpen={isModalOpen}
            onClose={handleModalClose}
            selectedPackage={selectedPackage}
            isArabic={isArabic}
          />
    </>
  )
}
